import React, { useState, useEffect } from "react";
import Layouts from "../../Layouts";
import DietService from "../../../Services/DietService";
import DoctorService from "../../../Services/DoctorService";
import ConsultationService from "../../../Services/ConsultationService";
import PatientService from "../../../Services/PatientService";
import { message } from "antd";
import { useLocation } from "react-router-dom";

// Sub-components
import DietPatientTable from "./DietPatientTable";
import DietPlanViewModal from "./DietPlanViewModal";
import DietPlanAddModal from "./DietPlanAddModal";
import DietPlanEditModal from "./DietPlanEditModal";

function DietPlan() {
  const location = useLocation();
  // Modal States
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);

  // Data States
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [editPatient, setEditPatient] = useState(null);
  const [dietPatientsList, setDietPatientsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Header/Clinic Data
  const [doctorData, setDoctorData] = useState(null);
  const [clinicDetails, setClinicDetails] = useState(null);
  const [hospitalAccountData, setHospitalAccountData] = useState(null);
  const [selectedPatientDetails, setSelectedPatientDetails] = useState(null);
  const [age, setAge] = useState(null);

  // Form States
  const [patientFormData, setPatientFormData] = useState({
    patientId: "",
    patientName: "",
    mobileNumber: "",
    emailId: "",
    dietTitle: "",
    fromDate: new Date().toISOString().split("T")[0],
    toDate: "",
    dietChartDays: 1,
    status: 1,
    mealConfirmation: 1,
    imageUploadRequired: 1,
    reminderNotification: 1,
    paymentRequiredYN: "N",
    amount: "",
  });

  // Search & Master Plan States
  const [masterPlans, setMasterPlans] = useState([]);
  const [selectedMasterPlanId, setSelectedMasterPlanId] = useState(null);
  const [patientSearchTerm, setPatientSearchTerm] = useState("");
  const [searchedPatients, setSearchedPatients] = useState([]);
  const [isPatientSearching, setIsPatientSearching] = useState(false);

  const storedLoginResult = sessionStorage.getItem("login_result");
  const loginResultObject = storedLoginResult ? JSON.parse(storedLoginResult) : null;

  useEffect(() => {
    fetchDietPatients();
    fetchMasterPlans();
    if (loginResultObject?.user?.userId) {
      fetchHeaderData();
    }
  }, []);

  // Auto-open assigned diet plan if ID is passed from notification
  useEffect(() => {
    if (location.state && location.state.autoOpenId) {
      const planId = location.state.autoOpenId;
      // We pass a minimal patient object with the ID
      // handleView will fetch the full details
      console.log("Auto opening plan view for ID:", planId);
      handleView({ id: planId });
      // Clear state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchDietPatients = async () => {
    if (!loginResultObject) return;
    setLoading(true);
    try {
      let pay = {
        accountId: loginResultObject.user.account_Id,
        clinicId: loginResultObject.user.clinic_Id,
      };
      let res = await DietService.getDietList(pay);
      setDietPatientsList(res.data || []);
    } catch (error) {
      console.error("Error fetching diet patients:", error);
      message.error("Failed to load patient list");
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterPlans = async () => {
    try {
      const payload = {
        accountId: loginResultObject?.user?.account_Id,
        clinicId: loginResultObject?.user?.clinic_Id,
        status: 1,
      };
      const res = await DietService.dietPlanHeaderList(payload);
      setMasterPlans(res.data || []);
    } catch (error) {
      console.error("Error fetching master plans:", error);
    }
  };

  const fetchHeaderData = async () => {
    try {
      const docRes = await DoctorService.getDoctorData(loginResultObject.user.userId);
      const docData = docRes?.data[0];
      setDoctorData(docData);

      if (docData?.account_Id) {
        const hospRes = await DoctorService.getHospitalAccountData(docData.account_Id);
        setHospitalAccountData(hospRes?.data[0]);
      }

      if (docData?.clinic_Id) {
        const clinicRes = await ConsultationService.getClinicData(docData.clinic_Id);
        setClinicDetails(clinicRes?.data[0]);
      }
    } catch (error) {
      console.error("Error fetching header data:", error);
    }
  };

  const resetForm = () => {
    setPatientFormData({
      patientId: "",
      patientName: "",
      mobileNumber: "",
      emailId: "",
      dietTitle: "",
      fromDate: new Date().toISOString().split("T")[0],
      toDate: "",
      dietChartDays: 1,
      status: 1,
      mealConfirmation: 1,
      imageUploadRequired: 1,
      reminderNotification: 1,
      paymentRequiredYN: "N",
      amount: "",
    });
    setSelectedMasterPlanId(null);
    setPatientSearchTerm("");
    setSearchedPatients([]);
  };

  useEffect(() => {
    if (selectedMasterPlanId && masterPlans.length > 0) {
      const plan = masterPlans.find(p => p.id === selectedMasterPlanId);
      if (plan) {
        const noOfDays = plan.noOfDays || 1;
        setPatientFormData(prev => {
          const fromDate = prev.fromDate || new Date().toISOString().split("T")[0];
          const toDateObj = new Date(fromDate);
          toDateObj.setDate(toDateObj.getDate() + (parseInt(noOfDays) - 1));
          const toDate = toDateObj.toISOString().split("T")[0];
          return {
            ...prev,
            dietChartDays: noOfDays,
            toDate: toDate,
            dietTitle: plan.chartTitle
          };
        });
      }
    }
  }, [selectedMasterPlanId, masterPlans]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "fromDate") {
      const noOfDays = patientFormData.dietChartDays || 1;
      const fromDate = new Date(value);
      const toDateObj = new Date(fromDate);
      toDateObj.setDate(toDateObj.getDate() + (parseInt(noOfDays) - 1));
      const toDate = toDateObj.toISOString().split("T")[0];
      setPatientFormData((prev) => ({ ...prev, fromDate: value, toDate: toDate }));
    } else {
      setPatientFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePatientSearch = async () => {
    if (!patientSearchTerm) {
      message.error("Enter patient mobile number to filter");
      return;
    }
    const isValidMobile = /^\d{1,11}$/.test(patientSearchTerm);
    if (!isValidMobile) {
      message.error("Mobile number must contain digits only, with no spaces.");
      return;
    }
    setIsPatientSearching(true);
    try {
      const payload = { mobile: patientSearchTerm };
      const res = await PatientService.PatientListFilter(payload);
      setSearchedPatients(res.data || []);
      if ((res.data || []).length === 0) message.info("No patients found");
    } catch (error) {
      console.error("Error searching patients:", error);
      message.error("Failed to search patients");
    } finally {
      setIsPatientSearching(false);
    }
  };

  const handleSavePatient = async () => {
    if (!loginResultObject || !patientFormData.patientId || !selectedMasterPlanId) {
      message.warning("Please select a patient and a master plan");
      return;
    }
    setLoading(true);
    try {
      const masterRes = await DietService.dietPlanHeaderGet(selectedMasterPlanId);
      const masterData = masterRes.data;

      const fromDate = patientFormData.fromDate || new Date().toISOString().split("T")[0];
      const noOfDays = masterData.noOfDays || 1;
      const toDateObj = new Date(fromDate);
      toDateObj.setDate(toDateObj.getDate() + (parseInt(noOfDays) - 1));
      const toDate = toDateObj.toISOString().split("T")[0];

      const payload = {
        userId: loginResultObject.user.userId,
        patientId: patientFormData.patientId,
        chartTitle: masterData.chartTitle,
        fromDate: fromDate,
        toDate: toDate,
        noOfDays: noOfDays,
        planStatus: "Active",
        mealConfirmation: masterData.mealConfirmation || 1,
        imageUploadRequired: masterData.imageUploadRequired || 1,
        reminderNotification: masterData.reminderNotification || 1,
        accountId: loginResultObject.user.account_Id,
        clinicId: loginResultObject.user.clinic_Id,
        status: 1,
        createdBy: loginResultObject.user.userId,
        modifiedBy: loginResultObject.user.userId,
        paymentRequiredYN: patientFormData.paymentRequiredYN || "N",
        amount: patientFormData.paymentRequiredYN === "Y" ? parseFloat(patientFormData.amount || 0) : 0,
        patientDietPlanDetails: (masterData.dietPlanDetails || []).map((detail) => ({
          dayTitle: detail.dayTitle,
          dayStatus: detail.dayStatus || "Active Plan",
          sortOrder: detail.sortOrder,
          mealTime: detail.mealTime ? (detail.mealTime.length === 5 ? `${detail.mealTime}:00` : detail.mealTime) : "00:00:00",
          mealType: detail.mealType,
          meal: detail.meal,
          activity: detail.activity || "",
          notes: detail.notes || "",
        })),
      };

      await DietService.dietPlanSave(payload);
      if (patientFormData.paymentRequiredYN === "Y") {
        message.info("The plan will reflect on the patient side after the payment completion");
      } else {
        message.success("Patient diet plan assigned successfully");
      }
      setOpenModal(false);
      resetForm();
      fetchDietPatients();
    } catch (error) {
      console.error("Error saving patient diet plan:", error);
      message.error("Failed to save patient diet plan");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePatient = async () => {
    if (!loginResultObject || !editPatient) return;
    setLoading(true);
    try {
      const fromDate = patientFormData.fromDate || new Date().toISOString().split("T")[0];
      const noOfDays = patientFormData.dietChartDays || 1;
      const toDateObj = new Date(fromDate);
      toDateObj.setDate(toDateObj.getDate() + (parseInt(noOfDays) - 1));
      const toDate = toDateObj.toISOString().split("T")[0];

      const payload = {
        ...editPatient,
        ...patientFormData,
        id: editPatient.id,
        accountId: loginResultObject.user.account_Id,
        clinicId: loginResultObject.user.clinic_Id,
        chartTitle: patientFormData.dietTitle,
        fromDate: fromDate,
        toDate: toDate,
        noOfDays: noOfDays,
        planStatus: patientFormData.status,
        paymentRequiredYN: patientFormData.paymentRequiredYN || "N",
        amount: patientFormData.paymentRequiredYN === "Y" ? parseFloat(patientFormData.amount || 0) : 0,
        patientDietPlanDetails: (editPatient.patientDietPlanDetails || []).map(detail => ({
          ...detail,
          mealTime: detail.mealTime?.length === 5 ? `${detail.mealTime}:00` : detail.mealTime
        }))
      };

      await DietService.dietPlanUpdate(payload);
      message.success("Patient diet plan updated successfully");
      setOpenEditModal(false);
      resetForm();
      fetchDietPatients();
    } catch (error) {
      console.error("Error updating patient diet plan:", error);
      message.error("Failed to update patient diet plan");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (patient) => {
    setLoading(true);
    try {
      const res = await DietService.getDietById(patient.id);
      const planData = res.data;
      setSelectedPatient(planData);

      const pId = patient.patientId || planData.patientId;
      if (pId) {
        const patientRes = await PatientService.PatientListFilter({ patientId: pId });
        const details = patientRes.data[0];
        setSelectedPatientDetails(details);
        if (details?.ageDob) setAge(calculateAge(details.ageDob));
      }
      setOpenViewModal(true);
    } catch (error) {
      console.error("Error viewing patient:", error);
      message.error("Failed to load details");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (patient) => {
    setLoading(true);
    try {
      const res = await DietService.getDietById(patient.id);
      const dietData = res.data;
      setEditPatient(dietData);
      setPatientFormData({
        patientId: dietData.patientId,
        patientName: dietData.patientName || dietData.name,
        mobileNumber: dietData.mobileNumber || dietData.mobile,
        emailId: dietData.emailId || dietData.email,
        dietTitle: dietData.chartTitle || dietData.dietTitle,
        fromDate: dietData.fromDate || dietData.startDate || "",
        toDate: dietData.toDate || dietData.endDate || "",
        dietChartDays: dietData.noOfDays || 1,
        status: 1,
        mealConfirmation: dietData.mealConfirmation || 1,
        imageUploadRequired: dietData.imageUploadRequired || 1,
        reminderNotification: dietData.reminderNotification || 1,
        paymentRequiredYN: dietData.paymentRequiredYN || "N",
        amount: dietData.amount || "",
      });
      setOpenEditModal(true);
    } catch (error) {
      console.error("Error editing patient:", error);
      message.error("Failed to load details");
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dob) => {
    if (!dob || typeof dob !== "string") return null;
    const [day, month, year] = dob.split("/").map(Number);
    if (!day || !month || !year) return null;
    const today = new Date();
    let age = today.getFullYear() - year;
    const hasHadBirthdayThisYear = today.getMonth() + 1 > month || (today.getMonth() + 1 === month && today.getDate() >= day);
    if (!hasHadBirthdayThisYear) age -= 1;
    return age;
  };

  const formatTime12hr = (timeStr) => {
    if (!timeStr) return "--:--";
    if (timeStr.includes("AM") || timeStr.includes("PM")) return timeStr;
    const [hoursStr, minutesStr] = timeStr.split(":");
    let hours = parseInt(hoursStr, 10);
    const minutes = minutesStr ? minutesStr.substring(0, 2) : "00";
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  };

  const handleMealChange = (dayTitle, mealIndex, field, value) => {
    const updatedDetails = { ...editPatient };
    const meals = updatedDetails.patientDietPlanDetails || [];
    let count = 0;
    let targetIndex = -1;
    for (let i = 0; i < meals.length; i++) {
      if (meals[i].dayTitle === dayTitle) {
        if (count === mealIndex) { targetIndex = i; break; }
        count++;
      }
    }
    if (targetIndex !== -1) {
      updatedDetails.patientDietPlanDetails[targetIndex] = { ...updatedDetails.patientDietPlanDetails[targetIndex], [field]: value };
      setEditPatient(updatedDetails);
    }
  };

  const addMeal = (dayTitle) => {
    const updatedDetails = { ...editPatient };
    const meals = updatedDetails.patientDietPlanDetails || [];
    const newMeal = {
      dayTitle,
      dayStatus: "Active Plan",
      sortOrder: meals.filter((m) => m.dayTitle === dayTitle).length,
      mealTime: "08:00",
      mealType: "Breakfast",
      meal: "",
      activity: meals.find((m) => m.dayTitle === dayTitle)?.activity || "",
      notes: meals.find((m) => m.dayTitle === dayTitle)?.notes || "",
    };
    updatedDetails.patientDietPlanDetails = [...meals, newMeal];
    setEditPatient(updatedDetails);
  };

  const removeMeal = (dayTitle, mealIndex) => {
    const updatedDetails = { ...editPatient };
    const meals = updatedDetails.patientDietPlanDetails || [];
    let count = 0;
    let targetFlatIndex = -1;
    for (let i = 0; i < meals.length; i++) {
      if (meals[i].dayTitle === dayTitle) {
        if (count === mealIndex) { targetFlatIndex = i; break; }
        count++;
      }
    }
    if (targetFlatIndex !== -1) {
      updatedDetails.patientDietPlanDetails.splice(targetFlatIndex, 1);
      setEditPatient(updatedDetails);
    }
  };

  const handleDayFieldChange = (dayTitle, field, value) => {
    const updatedDetails = { ...editPatient };
    updatedDetails.patientDietPlanDetails = (updatedDetails.patientDietPlanDetails || []).map(meal => {
      if (meal.dayTitle === dayTitle) return { ...meal, [field]: value };
      return meal;
    });
    setEditPatient(updatedDetails);
  };

  const filteredPatients = dietPatientsList.filter((p) =>
    p.patientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.patientId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.mobileNumber?.includes(searchTerm)
  );

  return (
    <Layouts>
      <div className="w-full min-h-screen bg-white p-3 font-sans scrollwidth">
        <h2 className="text-base font-semibold text-black mb-1">Diet Patient List</h2>

        <DietPatientTable
          loading={loading}
          filteredPatients={filteredPatients}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleView={handleView}
          handleEdit={handleEdit}
          onAddNewPlan={() => { resetForm(); setOpenModal(true); }}
          onAssignQuickPlan={(p) => {
            resetForm();
            setPatientFormData(prev => ({
              ...prev,
              patientId: p.patientId,
              patientName: p.patientName || p.name,
              mobileNumber: p.mobileNumber || p.mobile,
              emailId: p.emailId || p.email
            }));
            setOpenModal(true);
          }}
        />

        <DietPlanViewModal
          isOpen={openViewModal}
          onClose={() => setOpenViewModal(false)}
          selectedPatient={selectedPatient}
          selectedPatientDetails={selectedPatientDetails}
          age={age}
          clinicDetails={clinicDetails}
          doctorData={doctorData}
          hospitalAccountData={hospitalAccountData}
          formatTime12hr={formatTime12hr}
        />

        <DietPlanAddModal
          isOpen={openModal}
          onClose={() => setOpenModal(false)}
          patientSearchTerm={patientSearchTerm}
          setPatientSearchTerm={setPatientSearchTerm}
          handlePatientSearch={handlePatientSearch}
          isPatientSearching={isPatientSearching}
          searchedPatients={searchedPatients}
          patientFormData={patientFormData}
          setPatientFormData={setPatientFormData}
          masterPlans={masterPlans}
          selectedMasterPlanId={selectedMasterPlanId}
          setSelectedMasterPlanId={setSelectedMasterPlanId}
          handleInputChange={handleInputChange}
          handleSavePatient={handleSavePatient}
          loading={loading}
        />

        <DietPlanEditModal
          isOpen={openEditModal}
          onClose={() => setOpenEditModal(false)}
          editPatient={editPatient}
          patientFormData={patientFormData}
          handleInputChange={handleInputChange}
          handleMealChange={handleMealChange}
          addMeal={addMeal}
          removeMeal={removeMeal}
          handleDayFieldChange={handleDayFieldChange}
          handleUpdatePatient={handleUpdatePatient}
          loading={loading}
        />
      </div>
    </Layouts>
  );
}

export default DietPlan;
